/**
 * Browser Web Speech API helpers for Quick Clarity dictation.
 * No cloud STT — recognition is handled by the browser (often vendor-backed).
 */

export function getSpeechRecognitionConstructor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function isSpeechRecognitionSupported() {
  return Boolean(getSpeechRecognitionConstructor());
}

/**
 * @param {object} options
 * @param {string} [options.lang]
 * @param {(payload: { transcript: string, isFinal: boolean }) => void} [options.onResult]
 * @param {(message: string) => void} [options.onError]
 * @param {() => void} [options.onEnd]
 * @returns {{ start: () => void, stop: () => void, abort: () => void } | null}
 */
export function createSpeechRecognition({ lang, onResult, onError, onEnd } = {}) {
  const Recognition = getSpeechRecognitionConstructor();
  if (!Recognition) return null;

  const recognition = new Recognition();
  recognition.continuous = false;
  recognition.interimResults = true;
  recognition.lang = lang || (typeof navigator !== "undefined" && navigator.language) || "en-US";
  recognition.maxAlternatives = 1;

  recognition.onresult = (event) => {
    if (!onResult) return;
    let interim = "";
    let finalChunk = "";
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const result = event.results[i];
      const text = result?.[0]?.transcript || "";
      if (!text) continue;
      if (result.isFinal) finalChunk += text;
      else interim += text;
    }
    if (finalChunk) onResult({ transcript: finalChunk, isFinal: true });
    if (interim) onResult({ transcript: interim, isFinal: false });
  };

  recognition.onerror = (event) => {
    const code = event?.error || "unknown";
    if (code === "aborted" || code === "no-speech") {
      // Soft ends — caller may clear listening without alarming the user.
      if (onError) onError(code);
      return;
    }
    if (onError) {
      if (code === "not-allowed" || code === "service-not-allowed") {
        onError("Microphone permission is required to dictate. You can keep typing.");
      } else if (code === "network") {
        onError("Dictation needs a network connection in this browser. You can keep typing.");
      } else {
        onError("Dictation is unavailable right now. You can keep typing.");
      }
    }
  };

  recognition.onend = () => {
    if (onEnd) onEnd();
  };

  return {
    start() {
      try {
        recognition.start();
      } catch (err) {
        // Starting while already started throws in some browsers.
        if (onError) onError("Dictation is unavailable right now. You can keep typing.");
      }
    },
    stop() {
      try {
        recognition.stop();
      } catch (_) {
        /* ignore */
      }
    },
    abort() {
      try {
        recognition.abort();
      } catch (_) {
        /* ignore */
      }
    }
  };
}

/**
 * Append dictated text into an existing entry, respecting maxChars.
 * @param {string} current
 * @param {string} chunk
 * @param {number} maxChars
 */
export function mergeDictationText(current, chunk, maxChars) {
  const base = String(current || "");
  const addition = String(chunk || "").trim();
  if (!addition) return base.slice(0, maxChars);
  const needsSpace = base.length > 0 && !/\s$/.test(base);
  const next = needsSpace ? `${base} ${addition}` : `${base}${addition}`;
  return next.slice(0, maxChars);
}
